import React, { useEffect, useMemo, useState } from 'react';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useQuery } from 'hooks/useQuery';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import LicenceStore from 'state/LicenceStore';
import { Loader } from 'components/atoms/Loader';
import { useLang } from 'models/langContext';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EDictionaryTypes } from 'models/dictionary';
import { inNextTick } from 'utils/stateUtils';
import { useVaryLicence } from './hooks';
import { LicenceVaryEdit } from './licenceVaryEdit';
import { useLegalEntities } from '../hooks';
import { Elabels, getBreadcrumbsBasic } from 'pages/CaseManagementPage/CasesPage/helpers';

const LegalEntityCreateLicenceVaryPage = () => {
  const { t } = useTranslations();
  const { pathname, state: routerState }: any = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isFirearmDataLoading, setIsFirearmDataLoading] = useState(true);
  const [hasLoadedWeapons, setHasLoadedWeapons] = useState(false);
  const [hasHydratedWeapons, setHasHydratedWeapons] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<any[]>([]);

  const navigate = useNavigate();
  const query = useQuery();
  const {
    id = '',
    licenceId = '',
    caseId = '',
  } = useParams<{
    id: string;
    licenceId: string;
    caseId: string;
  }>();
  const type: string | null = query.get('type')?.trim() || null;
  const expand: string | null = query.get('expand');
  const { getProperties } = useGlobalProperty();
  const { selectedLanguage, translations } = useLang();
  const { loadDictionaries } = useVaryLicence();

  const { getLicenceData, licenceDetails } = useVaryLicence();

  const {
    getLicenceAttachmentsData,
    licenceAttachments,
    getLicenceWeaponData,
    licenceWeapon,
  } = useLegalEntities();

  const isFromLE = useMemo(
    () =>
      pathname.indexOf(
        ERouteLinks.LegalEntityCreateLicenceVaryCase.replace(':id', id)
          .replace(':licenceId', licenceId)
          .replace(':caseId', caseId),
      ) !== -1,
    [pathname, id, licenceId],
  );

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.applicationProcessTypes]);
      getProperties();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      if (licenceId) {
        setIsLoading(true);
        setIsFirearmDataLoading(true);
        setHasLoadedWeapons(false);
        setHasHydratedWeapons(false);
        const isReturningFromWeaponLinking = (routerState as any)?.saveToStory === true;
        if (!isReturningFromWeaponLinking) {
          LicenceStore.clearStore();
        }
        await Promise.all([
          getLicenceData(licenceId),
          getLicenceAttachmentsData(licenceId),
          getLicenceWeaponData(licenceId),
        ]);
        setHasLoadedWeapons(true);
        setIsFirearmDataLoading(false);
        setIsLoading(false);
      }
    })();
  }, [licenceId]);

  useEffect(() => {
    inNextTick(() => {
      const firearmLinkToBeRemovedIds =
        LicenceStore.getFormFirearmLinkToBeRemovedIds(licenceId);
      const sourceWeapons = Array.isArray(licenceWeapon) ? licenceWeapon : [];
      const filteredWeapons = firearmLinkToBeRemovedIds.length
        ? sourceWeapons.filter((item) => !firearmLinkToBeRemovedIds.includes(item.uniqueId))
        : sourceWeapons;

      LicenceStore.setFormFirearmCurrentLink(filteredWeapons, licenceId);
      setHasHydratedWeapons(true);
    });
  }, [licenceWeapon, licenceId, hasLoadedWeapons, hasHydratedWeapons]);

  useEffect(() => {
    if (licenceAttachments?.content.length) {
      const attachmentsToDeleteIds =
        LicenceStore.getFormLicenceAttachmentsToDeleteIds(licenceId);
      if (attachmentsToDeleteIds.length) {
        const attachmentWithoutDelete = licenceAttachments?.content.filter(
          (item) => !attachmentsToDeleteIds.includes(item.uniqueId),
        );
        LicenceStore.setFormLicenceAttachmentsData(attachmentWithoutDelete);
      } else {
        LicenceStore.setFormLicenceAttachmentsData(licenceAttachments?.content);
      }
    }
  }, [licenceAttachments]);

  useEffect(() => {
    if (translations) {
      const baseBreadcrumbs = getBreadcrumbsBasic(isFromLE, type, t, false, id);

      setBreadcrumbs([
        ...baseBreadcrumbs,
        {
          label: t(Elabels.caseDetailsHeader),
          route: isFromLE
            ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`
            : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`,
        },
      ]);
    }
  }, [isFromLE, expand, id, licenceId, type, translations]);

  const backLink = () => {
    LicenceStore.clearStore();
    if (isFromLE) {
      navigate(
        `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`,
      );
    } else {
      navigate(ERouteLinks.CasesDetailsPage.replace(':caseId', caseId));
    }
  };

  const errorType = () => {
    if (isFromLE) {
      navigate(ERouteLinks.LegalEntities);
    } else {
      navigate(ERouteLinks.LicencesPage);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[...breadcrumbs, t('legalEntity.licenceDetails.varyLicence.header')]}
        onClickLink={() => LicenceStore.clearStore()}
      />
      <StyledPageTitle variant="h4">
        {t('legalEntity.licenceDetails.varyLicence.header')}
      </StyledPageTitle>
      {isLoading && <Loader isLoading />}
      {!isLoading &&
        !isFirearmDataLoading &&
        licenceDetails &&
        licenceAttachments &&
        licenceWeapon && (
          <LicenceVaryEdit
            id={licenceDetails.owner.legalUniqueId}
            caseId={caseId}
            licenceDetails={licenceDetails}
            licenceTemplateId={licenceDetails.weaponLicenceTemplateUniqueId}
            backLink={backLink}
            errorType={errorType}
            breadcrumbs={breadcrumbs}
          />
        )}
    </>
  );
};

export { LegalEntityCreateLicenceVaryPage };
